import { useEffect, useMemo, useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { comboLabel, useRefData, type Combination } from '../../lib/refdata';
import type { Commitment, Vendor } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function Commitments() {
  const { persona } = useAuth();
  const { fy, voteByCode } = useRefData();
  const vote = voteByCode(persona?.voteCode);
  const [showCreate, setShowCreate] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const listQ = useApiQuery<{ items: Commitment[] }>(
    vote ? `/api/v1/commitments/?voteId=${vote.id}&limit=100` : null,
  );
  const policyQ = useApiQuery<{ psRequiredAbove: number }>('/api/v1/commitments/policy');

  const canCreate = persona?.roles.includes('MDA_PROCUREMENT_OFFICER') ?? false;
  const canApproveHof = persona?.roles.includes('MDA_HEAD_FINANCE') ?? false;
  const canApprovePs = persona?.roles.includes('MDA_PS') ?? false;
  const threshold = policyQ.data?.psRequiredAbove ?? 250_000;

  const approve = async (c: Commitment) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/commitments/${c.id}/approve`);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Approval failed');
    }
  };

  const needsHof = (c: Commitment) => c.status === 'FUNDS_CHECKED' && !(c.approvals ?? []).length;
  const needsPs = (c: Commitment) =>
    c.status === 'FUNDS_CHECKED' && (c.approvals ?? []).some((a) => a.role === 'MDA_HEAD_FINANCE') &&
    Number.parseFloat(c.amount) > threshold;

  if (!vote) return <ErrorNote message="No vote bound to this persona." />;

  return (
    <div>
      <PageHeader
        title="Commitments & purchase orders"
        subtitle={`Budget cover is reserved before any obligation — PS second approval above ${scr(threshold)}`}
        actions={
          canCreate ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + New commitment
            </button>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No commitments yet" hint="Create one to reserve budget cover for a supplier obligation." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Supplier</th>
                <th className="table-head">Purpose</th>
                <th className="table-head text-right">Amount</th>
                <th className="table-head text-right">Liquidated</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{c.refNumber}</td>
                  <td className="table-cell">{c.vendor?.name ?? '—'}</td>
                  <td className="table-cell max-w-md">
                    <div className="truncate" title={c.purpose}>
                      {c.purpose}
                    </div>
                    {c.contractRef ? <div className="text-xs text-slate-400">{c.contractRef}</div> : null}
                  </td>
                  <td className="table-cell text-right tabular-nums">{scr(c.amount)}</td>
                  <td className="table-cell text-right tabular-nums">{scr(c.liquidatedAmount)}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.status} />
                    {(c.approvals ?? []).length > 0 ? (
                      <div className="mt-1 text-[11px] text-slate-400">
                        {(c.approvals ?? []).map((a) => a.role.replaceAll('_', ' ')).join(' · ')}
                      </div>
                    ) : null}
                  </td>
                  <td className="table-cell text-right">
                    {canApproveHof && needsHof(c) ? (
                      <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => approve(c)}>
                        Approve (Head of Finance)
                      </button>
                    ) : canApprovePs && needsPs(c) ? (
                      <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => approve(c)}>
                        Approve (PS)
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && fy ? (
        <CreateModal
          voteId={vote.id}
          fyId={fy.id}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateModal({ voteId, fyId, onClose, onDone }: { voteId: string; fyId: string; onClose: () => void; onDone: () => void }) {
  const combosQ = useApiQuery<{ items: Combination[] }>(`/api/v1/coa/combinations?voteId=${voteId}&active=true&limit=100`);
  const vendorsQ = useApiQuery<{ items: Vendor[] }>('/api/v1/commitments/vendors?status=ACTIVE&limit=100');
  const [form, setForm] = useState({ coaCombinationId: '', vendorId: '', amount: '', purpose: '', contractRef: '' });
  const [check, setCheck] = useState<{ availableToCommit: number; wouldPass: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const amountNum = useMemo(() => Number.parseFloat(form.amount), [form.amount]);

  useEffect(() => {
    setCheck(null);
    if (!form.coaCombinationId || !Number.isFinite(amountNum) || amountNum <= 0) return;
    const t = setTimeout(() => {
      api
        .post<{ availableToCommit: number; requested: number; wouldPass: boolean }>('/api/v1/commitments/check', {
          fiscalYearId: fyId,
          coaCombinationId: form.coaCombinationId,
          amount: amountNum,
        })
        .then(setCheck)
        .catch(() => setCheck(null));
    }, 350);
    return () => clearTimeout(t);
  }, [form.coaCombinationId, amountNum, fyId]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/commitments/', {
        fiscalYearId: fyId,
        coaCombinationId: form.coaCombinationId,
        voteId,
        vendorId: form.vendorId || undefined,
        amount: amountNum,
        purpose: form.purpose,
        contractRef: form.contractRef || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create commitment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New commitment (reserves budget cover)" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Budget line">
          <select
            className="field-input"
            value={form.coaCombinationId}
            onChange={(e) => setForm({ ...form, coaCombinationId: e.target.value })}
          >
            <option value="">Select budget line…</option>
            {(combosQ.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {comboLabel(c)}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Supplier">
            <select className="field-input" value={form.vendorId} onChange={(e) => setForm({ ...form, vendorId: e.target.value })}>
              <option value="">Select supplier…</option>
              {(vendorsQ.data?.items ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Amount (SCR)">
            <input
              className="field-input"
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          </Field>
        </div>
        {check ? (
          <div
            className={`rounded-lg px-3.5 py-2.5 text-sm ${
              check.wouldPass ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
            }`}
          >
            {check.wouldPass ? '✓ Funds check passes' : '✗ Insufficient budget cover'} — available to commit:{' '}
            <strong>{scr(check.availableToCommit)}</strong>
          </div>
        ) : null}
        <Field label="Purpose">
          <textarea
            className="field-input"
            rows={2}
            value={form.purpose}
            onChange={(e) => setForm({ ...form, purpose: e.target.value })}
            placeholder="e.g. Accommodation for visiting delegation, 14–18 Jul 2026"
          />
        </Field>
        <Field label="Contract reference (optional)">
          <input
            className="field-input"
            value={form.contractRef}
            onChange={(e) => setForm({ ...form, contractRef: e.target.value })}
            placeholder="e.g. MTC/ACC/2026/031"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !form.coaCombinationId || !Number.isFinite(amountNum) || amountNum <= 0 || form.purpose.length < 3}
          >
            {busy ? 'Reserving funds…' : 'Create commitment'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
